<template>
  <vs-wrapper>
    <v-menu v-model="showingPdfMenu" offset-y>
      <template #activator="{}">
        <v-row dense justify="end" align="center">
          <v-col :class="props.isSingleLine ? 'py-0' : ''">
            <vs-button
              :icon="primaryAction.icon"
              :label="primaryAction.text"
              full-width
              :loading="
                (isActing !== null && isActing !== ActionType.DOWNLOAD_PDF) ||
                (isActing === ActionType.DOWNLOAD_PDF &&
                  primaryAction.action.type === ActionType.DOWNLOAD_PDF)
              "
              @click="performAction(primaryAction.action.type, primaryAction.action.value)"
            />
          </v-col>

          <v-col v-if="props.isSingleLine && menuActions.length > 0" class="shrink py-0">
            <vs-menu data-test="action-bar-overflow-btn" :items="menuActions" />
          </v-col>
        </v-row>
      </template>
      <v-list data-test="pdf-list">
        <v-list-item
          v-for="format in pdfTemplates"
          :key="format.id"
          @click="performAction(ActionType.DOWNLOAD_PDF, format.id)"
        >
          <v-list-item-title>{{ format.name }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-row v-if="!props.isSingleLine" justify="center" class="mt-2">
      <v-col v-for="item in secondaryActions" :key="item.text" class="shrink align-center">
        <vs-button
          class="btn mx-4"
          :icon="item.icon"
          type="secondary"
          :loading="
            (isActing !== null && isActing !== ActionType.DOWNLOAD_PDF) ||
            (isActing === ActionType.DOWNLOAD_PDF && item.action.type === ActionType.DOWNLOAD_PDF)
          "
          @click="performAction(item.action.type, item.action.value)"
        />
        <vs-text class="btn-text">{{ item.text }}</vs-text>
      </v-col>

      <v-col v-if="menuActions.length > 0" class="shrink align-center">
        <vs-menu :items="menuActions" data-test="readonly-more-button" class="btn mx-4" />
        <vs-text class="btn-text">More</vs-text>
      </v-col>
    </v-row>
  </vs-wrapper>
</template>

<script setup lang="ts">
import Job, { ACTIVE, CANCELLED, COMPLETED, PAUSED, UNCANCELLED } from '@/models/job'
import { useStore } from 'vuex-composition-helpers'
import { computed, ref } from 'vue'
import VsMenu from '@/components/vision/VsMenu.vue'
import VsButton from '@/components/vision/VsButton.vue'
import type { User as UserAuth0 } from '@auth0/auth0-spa-js/dist/typings/global'
import { getInstance } from '@/auth'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import { ActionType } from '@/components/tickets/ActionBarActions/ActionTypes'
import VsText from '@/components/vision/VsText.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import { ActionMenuItem } from '@/components/tickets/ActionBarActions/ActionMenuItem'
import { useRoute, useRouter } from 'vue-router/composables'
import { useCanJobBeEdited } from '@/components/tickets/useCanJobBeEdited'
import { useJobSearchRouteContext } from '@/common/useJobSearchRouteContext'
import Company from '@/models/company'
import { saveFile } from '@/utils/save-file'
import { getJobPdf } from '@/api/jobs'
import { SnackType } from '@/components/vision/SnackBarType'

const props = defineProps<{
  jobId: number
  company: Company | undefined
  isSaving?: boolean
  isSingleLine: boolean
}>()

const emit = defineEmits(['on-click', 'handle-snackbar'])

const router = useRouter()
const route = useRoute()
const { batchRouteName } = useJobSearchRouteContext()

const jobBottomSheetStatesStore = useJobBottomSheetStatesStore()
const auth: { user: UserAuth0 } = getInstance()
const store = useStore()

const clearTicketsError = () => {
  store.commit('tickets/CLEAR_TICKETS_ERROR')
}

const getJobById = computed<(jobNumber: number) => Job>(() => store.getters['jobs/getById'])
const canSetJobState = computed<(hubId: string) => boolean>(
  () => store.getters['permissions/canSetJobState'],
)
const canAssignJob = computed<(hubId: string) => boolean>(
  () => store.getters['permissions/canAssignResourceToJob'],
)
const canCancelJob = computed<(hubId: string, userIsRequesterOnUnassignedJob: boolean) => boolean>(
  () => store.getters['permissions/canCancelJob'],
)
const canReopenJob = computed<(hubId: string, jobAccountId: string) => boolean>(
  () => store.getters['permissions/canReopenJob'],
)
const canArchiveJob = computed<(hubId: string, accountId: string) => boolean>(
  () => store.getters['permissions/canArchiveJob'],
)
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch'],
)
const canViewBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canViewBatch'],
)
const canViewAddToBatchSelectionPage = computed<boolean>(
  () => store.getters['permissions/canViewAddToBatchSelectionPage'],
)
const canJobBeEdited = useCanJobBeEdited()

const isActing = ref<ActionType | null>(null)
const numberOfItemsOutsideMore = computed(() => {
  if (props.isSingleLine) {
    return 0
  } else {
    if (otherActions.value.length === 4) {
      return 4
    }
    return 3
  }
})
const job = computed(() => getJobById.value(props.jobId))

const pdfTemplates = computed(() =>
  (
    props.company?.pdfFormats?.filter((p) => job.value.activity?.pdfFormatIds.includes(p.id)) ?? []
  ).sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)),
)
const hasPdfTemplates = computed(() => pdfTemplates.value.length > 0)
const showingPdfMenu = ref(false)

const actions: Record<string, ActionMenuItem> = {
  edit: {
    action: { type: ActionType.EDIT },
    text: 'Edit',
    icon: 'mdi-pencil-outline',
  },
  pause: {
    action: { type: ActionType.STATUS_CHANGE, value: PAUSED },
    text: 'Pause',
    icon: 'mdi-pause',
  },
  addToBatch: {
    action: { type: ActionType.ADD_TO_BATCH },
    text: 'Add to batch',
    icon: 'drive_folder_upload',
  },
  addToProject: {
    action: { type: ActionType.ADD_TO_PROJECT },
    text: 'Add to project',
    icon: 'drive_folder_upload',
  },
  viewProject: {
    action: { type: ActionType.VIEW_PROJECT },
    text: 'View project',
    icon: 'folder_open',
  },
  viewBatch: {
    action: { type: ActionType.VIEW_BATCH },
    text: 'View batch',
    icon: 'mdi-wallet-bifold-outline',
  },
  removeFromProject: {
    action: { type: ActionType.REMOVE_FROM_PROJECT },
    text: 'Remove from project',
    icon: 'delete_outline',
  },
  removeFromBatch: {
    action: { type: ActionType.REMOVE_FROM_BATCH },
    text: 'Remove from batch',
    icon: 'delete_outline',
  },
  sendNotification: {
    action: { type: ActionType.SEND_NOTIFICATION },
    text: 'Notify',
    icon: 'send',
  },
  clone: {
    action: { type: ActionType.CLONE },
    text: 'Clone',
    icon: 'content_copy',
  },
  focusResource: {
    action: { type: ActionType.FOCUS_RESOURCE },
    text: 'Assign job to...',
    icon: '',
  },
  complete: {
    action: { type: ActionType.STATUS_CHANGE, value: COMPLETED },
    text: 'Mark as Completed',
    icon: 'mdi-checkbox-marked-outline',
  },
  cancel: {
    action: { type: ActionType.STATUS_CHANGE, value: CANCELLED },
    text: 'Mark as Cancelled',
    icon: 'highlight_off',
  },
  unCancel: {
    action: { type: ActionType.STATUS_CHANGE, value: UNCANCELLED },
    text: 'Reopen',
    icon: 'mdi-backup-restore',
  },
  archive: {
    action: { type: ActionType.ARCHIVE },
    text: 'Archive',
    icon: 'mdi-archive-arrow-down-outline',
  },
  unarchive: {
    action: { type: ActionType.UNARCHIVE },
    text: 'Unarchive',
    icon: 'mdi-archive-arrow-up-outline',
  },
  activate: {
    action: { type: ActionType.STATUS_CHANGE, value: ACTIVE },
    text: 'Mark as Active',
    icon: '',
  },
  downloadPdf: {
    action: { type: ActionType.DOWNLOAD_PDF },
    text: 'Download as PDF',
    icon: 'picture_as_pdf',
  },
}

/*
 * The action that should be shown in the big blue button
 */
const primaryAction = computed(() => {
  if (job.value.isArchived && canArchiveJob.value(job.value.hubId, job.value.accountId)) {
    return actions.unarchive
  }

  if (showStatusActions.value) {
    if (job.value.canGoActive) {
      return actions.activate
    }
    return actions.complete
  }

  if (showAssignOption.value) {
    return actions.focusResource
  }

  return actions.sendNotification
})

const otherActions = computed(() => {
  const commonActions: ActionMenuItem[] = []

  if (showPauseAction.value) {
    commonActions.push(actions.pause)
  }

  if (showEditAction.value) {
    commonActions.push(actions.edit)
  }

  commonActions.push(actions.clone)
  commonActions.push(actions.sendNotification)

  if (showCompleteAction.value) {
    commonActions.push(actions.complete)
  }

  if (showCancelAction.value) {
    commonActions.push(actions.cancel)
  }

  if (showUnCancelAction.value) {
    commonActions.push(actions.unCancel)
  }

  if (showArchiveAction.value) {
    commonActions.push(actions.archive)
  }

  if (showUnArchiveAction.value) {
    commonActions.push(actions.unarchive)
  }

  if (!job.value.projectId) {
    commonActions.push(actions.addToProject)
  } else {
    commonActions.push(actions.viewProject)
    commonActions.push(actions.removeFromProject)
  }

  if (job.value.invoiceId && canEditBatch.value(job.value.serviceProviderBusinessUnitId)) {
    commonActions.push(actions.removeFromBatch)
  }
  if (!job.value.invoiceId && canViewAddToBatchSelectionPage.value) {
    commonActions.push(actions.addToBatch)
  }
  if (job.value.invoiceId && canViewBatch.value(job.value.serviceProviderBusinessUnitId)) {
    commonActions.push(actions.viewBatch)
  }

  if (hasPdfTemplates.value) {
    commonActions.push(actions.downloadPdf)
  }

  return commonActions.filter((action) => action !== primaryAction.value)
})

/*
 * The list of actions that should be shown beside the "more" button
 */
const secondaryActions = computed(() => otherActions.value.slice(0, numberOfItemsOutsideMore.value))

/*
 * The list of actions that should be in the "More" menu, in the format expected by the vs-menu component.
 * Status-related options are sorted to the top and separated with a divider.
 */
const menuActions = computed(() => {
  const menuItems = otherActions.value.slice(numberOfItemsOutsideMore.value).map((x) => ({
    ...x,
    onClick: () => performAction(x.action.type, x.action.value),
  }))

  const statusActions = menuItems.filter((x) => x.action.type === ActionType.STATUS_CHANGE)
  const nonStatusActions = menuItems.filter((x) => x.action.type !== ActionType.STATUS_CHANGE)

  if (statusActions.length > 0 && nonStatusActions.length > 0) {
    return [...statusActions, { divider: true }, ...nonStatusActions]
  } else {
    return [...statusActions, ...nonStatusActions]
  }
})

const showStatusActions = computed(
  () =>
    !job.value.isArchived &&
    canSetJobState.value(job.value.hubId) &&
    !job.value.awaitingSchedule &&
    !job.value.isCompleted &&
    !job.value.isCancelled,
)

const showPauseAction = computed(() => {
  return job.value.isActive
})

const showEditAction = computed(
  () =>
    !props.isSingleLine && canJobBeEdited(job.value.status, job.value.isArchived, job.value.hubId),
)

const showCancelAction = computed(() => {
  const userIsRequesterOnUnassignedJob =
    job.value.awaitingSchedule && job.value.customerSid === auth.user.sub
  return (
    !job.value.isArchived &&
    canCancelJob.value(job.value.hubId, userIsRequesterOnUnassignedJob) &&
    !job.value.isCancelled
  )
})
const showUnCancelAction = computed(() => {
  return (
    !job.value.isArchived &&
    canReopenJob.value(job.value.hubId, job.value.accountId) &&
    job.value.isCancelled
  )
})
const showArchiveAction = computed(
  () => canArchiveJob.value(job.value.hubId, job.value.accountId) && !job.value.isArchived,
)
const showUnArchiveAction = computed(
  () => canArchiveJob.value(job.value.hubId, job.value.accountId) && job.value.isArchived,
)
const showCompleteAction = computed(
  () => canSetJobState.value(job.value.hubId) && !job.value.isCancelled && !job.value.isArchived,
)
const showAssignOption = computed(
  () =>
    props.isSingleLine &&
    job.value.awaitingSchedule &&
    canAssignJob.value(job.value.hubId) &&
    !job.value.isArchived,
)

const viewBatch = async () => {
  if (!job.value.invoiceId) return
  await router.push({
    name: batchRouteName.value,
    params: { batchId: job.value.invoiceId },
  })
}

async function downloadPdf(pdfFormatId: string) {
  try {
    saveFile(await getJobPdf(job.value.uuid, pdfFormatId))
  } catch {
    emit('handle-snackbar', {
      type: SnackType.ERROR,
      messageKey: 'pdfDownloadError',
    })
  }
}

const performCommonAction = async (action: ActionType, value: any): Promise<boolean> => {
  switch (action) {
    case ActionType.CLONE:
      await router.push({
        name: (route.meta?.cloneRouteName as string | undefined) ?? route.name ?? undefined,
        params: { id: 'new', hubId: job.value.hubId },
        query: { fromJob: job.value.id.toString() },
      })
      return true
    case ActionType.ADD_TO_BATCH:
      jobBottomSheetStatesStore.setJobBottomSheetStates({
        isBatchBottomSheetVisible: true,
      })
      return true
    case ActionType.ADD_TO_PROJECT:
      jobBottomSheetStatesStore.setJobBottomSheetStates({
        isProjectBottomSheetVisible: true,
      })
      return true
    case ActionType.VIEW_BATCH:
      await viewBatch()
      return true
    case ActionType.DOWNLOAD_PDF:
      if (pdfTemplates.value.length === 0) {
        return true
      } else if (pdfTemplates.value.length > 1 && !value) {
        showingPdfMenu.value = true
        return true
      } else {
        showingPdfMenu.value = false

        const pdfTemplate = value ?? pdfTemplates.value[0].id
        await downloadPdf(pdfTemplate)
      }
  }

  return false
}

/*
 * Some actions are the same regardless of the parent component, so these can be done immediately here.
 * For any action where the implementation is going to depend on the parent component, we want to let that
 * component handle it.
 */
const performAction = async (action: ActionType, value: any) => {
  clearTicketsError()
  isActing.value = action

  if (await performCommonAction(action, value)) {
    isActing.value = null
  } else {
    emit('on-click', {
      type: action,
      value,
      complete: () => {
        isActing.value = null
      },
    })
  }
}
</script>

<style scoped>
.btn {
  height: 48px;
  width: 48px;
}

.btn-text {
  text-align: center;
  color: var(--color-blue);
  font-weight: bold;
  width: 80px;
}
</style>
